import React from 'react';
import PropTypes from 'prop-types';
import { Input, Select } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Box, Flex } from 'atoms';
import {
	StyledModal,
	StyledForm,
	RemoveButton,
	SaveButton,
	ModalFormItem,
} from './CreateVehicleTypePageV2.styles';

const { Option } = Select;

function VehicleInfoModal({
	isModalVisible,
	modalForm,
	makerOptions,
	modalModelOptions,
	modalMaker,
	handleModalMakerChange,
	handleSaveChanges,
	handleRemoveVehicle,
	handleModalClose,
}) {
	return (
		<StyledModal
			title="Vehicle Information"
			open={isModalVisible}
			onCancel={handleModalClose}
			closeIcon={<CloseOutlined />}
			footer={[
				<RemoveButton key="remove" onClick={handleRemoveVehicle}>
					Remove Vehicle
				</RemoveButton>,
				<SaveButton key="save" type="primary" onClick={handleSaveChanges}>
					Save Changes
				</SaveButton>,
			]}
			width={800}
			centered
			maskClosable={false}
		>
			<StyledForm form={modalForm} layout="vertical">
				<Flex gap="16px" flexWrap="wrap">
					<Box flex="1" minWidth="300px">
						<ModalFormItem
							label="Maker/Manufacturer*"
							name="maker"
							rules={[{ required: true, message: 'Please select maker' }]}
						>
							<Select
								placeholder="Select"
								size="large"
								getPopupContainer={(trigger) => trigger.parentNode}
								onChange={handleModalMakerChange}
							>
								{makerOptions.map((maker) => (
									<Option key={maker} value={maker}>
										{maker}
									</Option>
								))}
							</Select>
						</ModalFormItem>
					</Box>

					<Box flex="1" minWidth="300px">
						<ModalFormItem
							label="Model Name*"
							name="modelName"
							rules={[{ required: true, message: 'Please select model name' }]}
						>
							<Select
								placeholder="Select"
								size="large"
								getPopupContainer={(trigger) => trigger.parentNode}
								disabled={!modalMaker}
								notFoundContent="Please select a maker first"
							>
								{modalModelOptions.map((model) => (
									<Option key={model} value={model}>
										{model}
									</Option>
								))}
							</Select>
						</ModalFormItem>
					</Box>
				</Flex>

				<Flex gap="16px" flexWrap="wrap">
					<Box flex="1" minWidth="300px">
						<ModalFormItem
							label="Capacity (in tonnes)*"
							name="capacity"
							rules={[
								{ required: true, message: 'Please enter capacity' },
								{
									type: 'number',
									min: 0.01,
									max: 999.99,
									message: 'Capacity must be between 0.01 and 999.99',
									transform: (value) => Number(value),
								},
							]}
						>
							<Input
								placeholder="12.50"
								size="large"
								type="number"
								step="0.01"
								min="0.01"
								max="999.99"
							/>
						</ModalFormItem>
					</Box>

					<Box flex="1" minWidth="300px">
						<ModalFormItem
							label="Length (in feet)*"
							name="length"
							rules={[
								{ required: true, message: 'Please enter length' },
								{
									type: 'number',
									min: 0.01,
									max: 999.99,
									message: 'Length must be between 0.01 and 999.99',
									transform: (value) => Number(value),
								},
							]}
						>
							<Input
								placeholder="10.50"
								size="large"
								type="number"
								step="0.01"
								min="0.01"
								max="999.99"
							/>
						</ModalFormItem>
					</Box>
				</Flex>

				<Flex gap="16px" flexWrap="wrap">
					<Box flex="1" minWidth="300px">
						<ModalFormItem
							label="Temperature Controlled*"
							name="tempControlled"
							rules={[
								{
									required: true,
									message: 'Please select temperature control',
								},
							]}
						>
							<Select
								placeholder="Select"
								size="large"
								getPopupContainer={(trigger) => trigger.parentNode}
							>
								<Option value="true">Yes</Option>
								<Option value="false">No</Option>
							</Select>
						</ModalFormItem>
					</Box>

					<Box flex="1" minWidth="300px">
						<ModalFormItem
							label="Driver Mode*"
							name="driverMode"
							rules={[{ required: true, message: 'Please select driver mode' }]}
						>
							<Select
								placeholder="Select"
								size="large"
								getPopupContainer={(trigger) => trigger.parentNode}
							>
								<Option value="Manual">Manual</Option>
								<Option value="Automatic">Automatic</Option>
							</Select>
						</ModalFormItem>
					</Box>
				</Flex>

				<Flex gap="16px" flexWrap="wrap">
					<Box flex="1" minWidth="300px">
						<ModalFormItem
							label="Fuel Type*"
							name="fuelType"
							rules={[{ required: true, message: 'Please select fuel type' }]}
						>
							<Select
								placeholder="Select"
								size="large"
								getPopupContainer={(trigger) => trigger.parentNode}
							>
								<Option value="Petrol">Petrol</Option>
								<Option value="Diesel">Diesel</Option>
								<Option value="Electric">Electric</Option>
								<Option value="CNG">CNG</Option>
							</Select>
						</ModalFormItem>
					</Box>
				</Flex>
			</StyledForm>
		</StyledModal>
	);
}

VehicleInfoModal.propTypes = {
	isModalVisible: PropTypes.bool.isRequired,
	modalForm: PropTypes.object.isRequired,
	makerOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
	modalModelOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
	modalMaker: PropTypes.string,
	handleModalMakerChange: PropTypes.func.isRequired,
	handleSaveChanges: PropTypes.func.isRequired,
	handleRemoveVehicle: PropTypes.func.isRequired,
	handleModalClose: PropTypes.func.isRequired,
};

VehicleInfoModal.defaultProps = {
	modalMaker: null,
};

export default VehicleInfoModal;
